import { computed, Injectable, signal, Signal } from '@angular/core';

/**
 * Tracks the stack of currently open modals so that only the topmost one is
 * interactive and painted above the others.
 *
 * When modals are nested (a `lib-modal` rendered inside another `lib-modal`'s
 * content), every overlay is a full-viewport `position: fixed` scroll container
 * with the same z-index. Without coordination the browser keeps two live
 * scrollbars and the front modal is not guaranteed to receive clicks. This
 * service hands each open modal a stable z-index based on its open order and
 * lets a modal know whether it is the topmost one.
 */
@Injectable({ providedIn: 'root' })
export class ModalStackService {
  /** Base z-index applied to the bottom-most modal overlay. */
  public static readonly BASE_Z_INDEX = 100;

  private readonly stack = signal<symbol[]>([]);

  /** Registers a newly opened modal using its identity token. */
  public register(token: symbol): void {
    this.stack.update((stack) => (stack.includes(token) ? stack : [...stack, token]));
  }

  /** Removes a modal from the stack when it closes or is destroyed. */
  public unregister(token: symbol): void {
    this.stack.update((stack) => stack.filter((item) => item !== token));
  }

  /**
   * The z-index for a given modal: deeper modals in the open order sit above
   * the ones opened before them. A not-yet-registered modal (its `register`
   * effect runs after the first render frame) is treated as the next top of
   * the stack so it never renders below an already-open modal.
   */
  public zIndex(token: symbol): Signal<number> {
    return computed(() => {
      const stack = this.stack();
      const index = stack.indexOf(token);
      return ModalStackService.BASE_Z_INDEX + (index === -1 ? stack.length : index);
    });
  }

  /**
   * Whether the given modal is the last (topmost) one opened. A not-yet-
   * registered modal is treated as topmost so it stays interactive during the
   * frame before its `register` effect runs.
   */
  public isTopmost(token: symbol): Signal<boolean> {
    return computed(() => {
      const stack = this.stack();
      const index = stack.indexOf(token);
      return index === -1 || index === stack.length - 1;
    });
  }
}
